import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { isUsuarioPresoLoja } from './utils/escopoUsuario';
import { getConfigRestrito, rotaPermitida, getLandingPerfil } from './utils/perfisRestritos';
import { podeVerProjetos } from './utils/acessoProjetos';
import { perfisDoModulo, perfisComAcessoTotal } from './utils/perfisDerivados';
import { PERFIS_MARKETING } from './utils/perfisMarketing';
import { useCapacidades } from './utils/capacidades';
import Login from './components/Login';
import EsqueciSenha from './components/EsqueciSenha';
import RedefinirSenha from './components/RedefinirSenha';
import Home from './components/home/Home';
import CalendarioAniversariantes from './components/home/CalendarioAniversariantes';
import ColaboradoresConsulta from './components/home/ColaboradoresConsulta';
import IndicadoresFormularios from './components/home/IndicadoresFormularios';
import Primeiro15Externo from './components/rh/formularios/RHFormsPrimeiro15_Externo';
import PesquisaExperienciaColaboradorExterno from './components/rh/formularios/RHFormsPesquisaExperienciaColaborador_Externo';
import PesquisaEscalaExterno from './components/rh/pesquisa-escala/RHFormsPesquisaEscala_Externo';
import PPIInscricao from './components/treinamento/ppi/PPIInscricao';
import PPIProvaPublica from './components/treinamento/ppi/PPIProvaPublica';
import PPIProjecao from './components/treinamento/ppi/PPIProjecao';
import Agradecimento from './components/Agradecimento';
import AdminDashboard from './components/paineladmin/AdminDashboard';
import ETLPanel from './components/ETLPanel';
import Fiscal from './components/fiscal/Fiscal';
import Financeiro from './components/financeiro/Financeiro';
import Contabilidade from './components/contabilidade/Contabilidade';
import RH from './components/rh/RH';
import Treinamento from './components/treinamento/Treinamento';
import Medicina from './components/medicina/Medicina';
import Projetos from './components/projetos/Projetos';
import Inscricao from './components/processoseletivo/funil/ProcessoSeletivoInscricao';
import ProcessoSeletivo from './components/processoseletivo/ProcessoSeletivo';
import ProcessoSeletivoTriagem from './components/processoseletivo/funil/ProcessoSeletivoTriagem';
import Vagas from './components/Vagas';
import Manutencao from './components/manutencao/Manutencao';
import Biblioteca from './components/biblioteca/Biblioteca';
import Compras from './components/compras/Compras';
import Logistica from './components/logistica/Logistica';
import Marketing from './components/marketing/Marketing';
import Demandas from './components/demandas/Demandas';
import DemandaDetalhe from './components/demandas/DemandaDetalhe';
import Chamados from './components/chamados/Chamados';
import ChamadosEtiquetaEntrada from './components/chamados/etiquetas/ChamadosEtiquetaEntrada';
import Operacional from './components/operacional/Operacional';


/**
 * Função Helper (Ajuda) para obter dados de autenticação
 * Centraliza a lógica de ler (e limpar) o localStorage.
 */
const getAuth = () => {
  const token = localStorage.getItem('authToken');
  const userDataString = localStorage.getItem('user');

  if (token && userDataString) {
    try {
      const user = JSON.parse(userDataString);
      // Confirma que temos um token E um utilizador com perfil
      if (user && user.perfil) {
        return { isLoggedIn: true, user };
      }
    } catch (e) {
      console.error("Erro ao processar dados do usuário:", e);
      // Se os dados estiverem corrompidos, limpamos
    }
  }

  // Se algo falhar, garante que o utilizador está deslogado
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  return { isLoggedIn: false, user: null };
};

// --- COMPONENTE QUE RASTREIA A NAVEGAÇÃO ---
const PageTracker = () => {
  const location = useLocation();

  React.useEffect(() => {
    const token = localStorage.getItem('authToken');
    
    // Ignora a rota de login para não gravar "undefined" antes de logar
    if (token && location.pathname !== '/login') {
      fetch('/api/logs/registrar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          acao: 'NAVEGACAO',
          detalhe: `Acessou: ${location.pathname}`
        })
      }).catch(err => console.log("Tracker error ignored:", err)); // Erros aqui são silenciosos
    }
  }, [location.pathname]); // Executa sempre que o caminho da URL muda

  return null; 
};

/**
 * Raiz do domínio (`/`) — quem chega aqui não escolheu uma tela, então nós
 * escolhemos por ele.
 *
 * ⚠️ POR QUE ISTO DEIXOU DE SER UM `Navigate` FIXO PARA `/vagas` (2026-08-04)
 * Todo atalho de tela inicial abre a RAIZ, não a página em que a pessoa estava:
 * o `start_url` do `public/manifest.json` é `"/"`, e o Safari o respeita ao
 * "Adicionar à Tela de Início" (iPhone) e "Adicionar ao Dock" (macOS). Com o
 * redirecionamento fixo, o atalho do Sr. Marco caía sempre na página pública de
 * vagas — que é um iframe de tela cheia, sem nenhum caminho para o login.
 *
 * Agora: com sessão válida, vai para o landing do perfil (o dele é /projetos);
 * sem sessão, segue para /vagas, que continua sendo o rosto público do domínio
 * para candidatos — comportamento inalterado para quem chega de fora.
 */
const EntradaRaiz = () => {
    const { isLoggedIn, user } = getAuth();
    if (isLoggedIn) return <Navigate to={getLandingPerfil(user.perfil)} replace />;
    return <Navigate to="/vagas" replace />;
};

/**
 * Aviso de "sem permissão" para o beco sem saída dos perfis restritos.
 *
 * Existe porque um perfil restrito cujo ÚNICO destino é uma rota que o portão
 * por usuário nega não tem para onde ser redirecionado — qualquer `Navigate`
 * daqui vira laço infinito. Em vez de piscar a tela para sempre, ele lê o que
 * houve e a quem recorrer.
 */
const SemPermissao = () => (
    <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', backgroundColor: '#f8fafc',
    }}>
        <div style={{
            maxWidth: '440px', textAlign: 'center', padding: '36px 32px',
            backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0',
        }}>
            <h1 style={{ margin: '0 0 12px', fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
                Sem permissão de acesso
            </h1>
            <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14.5px', lineHeight: 1.65 }}>
                Sua conta não tem acesso liberado a este conteúdo. Se acredita que deveria ter,
                fale com a equipe de dados.
            </p>
            <button
                onClick={() => { localStorage.removeItem('authToken'); localStorage.removeItem('user'); window.location.href = '/login'; }}
                style={{
                    padding: '11px 24px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                    backgroundColor: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: '14px',
                }}
            >
                Sair
            </button>
        </div>
    </div>
);

/**
 * Componente de Rota Privada OTIMIZADO
 */
const PrivateRoute = ({ children, allowedRoles, blockLoja, podeAcessar, capacidade }) => {
  // 1. Pega os dados de autenticação e a localização atual
  const { isLoggedIn, user } = getAuth();
  const location = useLocation();
  // ⚠️ O hook precisa rodar ANTES de qualquer `return` — regra dos hooks do
  //    React. Por isso ele vem aqui em cima, mesmo que a maioria das rotas não
  //    use `capacidade`. É uma requisição por página, compartilhada.
  const { caps, carregando: carregandoCaps } = useCapacidades();

  // 2. Se NÃO estiver logado...
  if (!isLoggedIn) {
    // Redireciona para /login
    // O 'state={{ from: location }}' guarda a página que o utilizador
    // estava a tentar aceder. O 'replace' evita que /login fique no histórico.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. Se ESTIVER logado, verifica as permissões
  const userRole = user.perfil;

  // Lógica de permissão simplificada:
  const isAdmin = userRole === 'Admin';

  // Portão dos PERFIS RESTRITOS (ex.: Coordenador de Qualidade): só enxergam um
  // conjunto fechado de módulos e NÃO têm Home. Qualquer rota fora do allowlist
  // — inclusive /home e as páginas soltas abertas a todo logado — é redirecionada
  // pro landing do perfil. Vem ANTES do allowedRoles para cobrir essas rotas
  // abertas; Admin nunca é restrito.
  const cfgRestrito = !isAdmin ? getConfigRestrito(userRole) : null;
  if (cfgRestrito && !rotaPermitida(userRole, location.pathname)) {
    return <Navigate to={cfgRestrito.landing} replace />;
  }
  
  // Portão por USUÁRIO — exceção ao padrão do portal, que autoriza por perfil.
  // A rota que passa `podeAcessar` decide sozinha quem entra (o Admin já é
  // liberado dentro da própria função). Hoje só o módulo Projetos usa: o
  // inventário é do CEO, e o perfil dele — Presidencia — é dele sozinho.
  if (podeAcessar && !podeAcessar(user)) {
    // ⚠️ LAÇO INFINITO se redirecionarmos um perfil RESTRITO daqui. Ele tem
    //    dois portões em série nesta mesma rota: o do perfil (que a permite) e
    //    o do usuário (que a nega). Mandá-lo para o landing devolve-o a esta
    //    rota, que nega de novo — /projetos → /home → /projetos → …
    //    Acontece com qualquer `Presidencia` fora da allowlist de IDs.
    //    Descoberto em 2026-08-04, ao criar um usuário de teste do perfil.
    if (cfgRestrito) return <SemPermissao />;
    return <Navigate to="/home" replace />;
  }

  // Se 'allowedRoles' não for definido (ex: /home), permite o acesso.
  // Se for definido, verifica se o perfil do utilizador está na lista.
  //
  // ⚠️⚠️ E, desde 18/09/2026, TAMBÉM pela CAPACIDADE do módulo: a rota passa
  //    `capacidade="chamados.modulo"` e quem recebeu essa concessão na matriz do
  //    Painel Admin entra, mesmo sem estar na lista. É o mesmo "ou" do backend
  //    (`authorizeCapacidadeOuPerfis`): a capacidade AMPLIA, nunca aperta — a
  //    lista continua valendo inteira, então ninguém perde acesso.
  //    ⚠️ Sem este ramo, conceder o módulo pela matriz não adiantava: o
  //    `PrivateRoute` mandava a pessoa para a Home SEM ERRO NENHUM, e quem
  //    concedeu concluía que a matriz não funciona. Medido com o perfil `DevOps`.
  const hasRequiredRole = !allowedRoles || allowedRoles.includes(userRole)
    || (capacidade && caps.has(capacidade));

  // ⚠️ Enquanto as capacidades não chegam, NÃO se decide: negar aqui mandaria
  //    para a Home quem tem acesso por concessão, e o redirecionamento é mudo.
  //    Só as rotas que usam `capacidade` esperam — as demais seguem na hora.
  if (capacidade && carregandoCaps) return null;

  // Módulos centrais (blockLoja): barra usuário preso à loja (ex.: Linhares),
  // mesmo via URL direta. Admin nunca é barrado.
  if (blockLoja && !isAdmin && isUsuarioPresoLoja(user)) {
    return <Navigate to="/home" replace />;
  }

  // 4. Tem permissão? (É admin OU tem o perfil necessário)
  if (isAdmin || hasRequiredRole) {
    return children; // Sim. Mostra a página.
  }

  // 5. Se chegou aqui, está logado MAS não tem permissão: manda pra Home.
  // (A tela "Acesso Negado" foi descontinuada — /home aceita qualquer logado.)
  return <Navigate to="/home" replace />;
};


function App() {
  return (
    <Router>
      <PageTracker />
      <Routes>
        {/* Raiz do domínio: o rosto público (vagas) para quem chega de fora, e o
            portal para quem já tem sessão. Ver EntradaRaiz. */}
        <Route path="/" element={<EntradaRaiz />} />
        {/* Rota pública de Login */}
        <Route
         path="/login"
         element={<Login />}
        />
        {/* Rotas públicas do fluxo "Esqueci a senha" (recuperação por WhatsApp) */}
        <Route
         path="/esqueci-senha"
         element={<EsqueciSenha />}
        />
        <Route
         path="/redefinir-senha/:token"
         element={<RedefinirSenha />}
        />
        {/* Rota pública de Login */}
        <Route
         path="/vagas"
         element={<Vagas />}
        />
        {/* Rota pública de Inscrição Candidato */}
        <Route
         path="/inscricao"
         element={<Inscricao />}
        />
        {/* Rota de Agradecimento Geral */}
        <Route 
          path="/agradecimento" 
          element={<PrivateRoute><Agradecimento /></PrivateRoute>} 
        />
        {/* --- Rotas Protegidas --- */}
        {/* /home : Acessível para qualquer um logado */}
        <Route
          path="/home"
          element={<PrivateRoute><Home /></PrivateRoute>}
        />
        {/* Calendário de aniversariantes — acessível a qualquer logado (igual à Home) */}
        <Route
          path="/aniversariantes"
          element={<PrivateRoute><CalendarioAniversariantes /></PrivateRoute>}
        />
        {/* Consulta de colaboradores ativos — acessível a qualquer logado */}
        <Route
          path="/colaboradores"
          element={<PrivateRoute><ColaboradoresConsulta /></PrivateRoute>}
        />
        {/* Indicadores de Auditoria & Treinamento — recorte por loja no backend */}
        <Route
          path="/indicadores"
          element={<PrivateRoute><IndicadoresFormularios /></PrivateRoute>}
        />
        {/* Não existe rota de manual: o Manual da Tela é uma GAVETA lateral, aberta
            pelo botão de ajuda de cada módulo (ver ModuloHeader › onAbrirManual).
            Havia aqui um /manual-ajuda que renderizava <HelpCenter /> sem `isOpen` —
            o componente devolve null nesse caso, então a página era uma tela em
            branco. Removido em 2026-08-05; nada apontava para ele. Se um dia o
            manual precisar de página própria, ela terá de passar isOpen e um
            onClose que navegue de volta — não basta montar o componente. */}

        {/* --- Módulo Auditoria — ENCERRADO em 2026-09-23 (DEM-00011, Fase 5,
            T26). O hub esvaziou na Fase 4 (T21: o único card, o Checklist,
            virou "Auditoria Financeira e de Estoques" no Operacional) e não
            sobrou nada para mostrar aqui — rota, Sidebar e o import acima
            saíram. Código do hub arquivado em `backup/auditoria/hub/` (git mv,
            com README de como religar). ⚠️ O que NÃO saiu: `/api/auditoria/
            checklist` e `/api/auditoria/versoes` (backend do Checklist,
            intocado — "muda de tela, não de rota") e o tag de módulo
            'auditoria' em perfisDerivados.js (autoriza `Diretoria Op` nessas
            rotas de API, independente de hub nenhum existir). --- */}
        {/* --- Módulo Biblioteca (paleta sienna/marrom): o acervo do portal, em
            duas prateleiras — os MANUAIS de processo do grupo (o 1º é o de
            Processos Financeiros, do Felipe) e a VIDEOTECA, o índice dos vídeos
            de procedimento que as telas de trabalho já tocam.
            Não confundir com o "Manual da Tela" (HelpCenter), que é a ajuda
            curta dentro de cada tela — ver o cabeçalho de Biblioteca.js.

            ⚠️ SEM blockLoja: é documentação, não tem dado recortado por loja —
               o gerente de plantão da loja é justamente o leitor-alvo.
            ⚠️ ABERTA A TODO LOGADO (DEM-00022, 30/09/2026): SEM `allowedRoles`,
               como a /home. O acervo (manuais de processo + Videoteca + Conecta)
               é leitura para a rede inteira, e quem publica o Conecta (Marketing)
               precisa ver o resultado do que publicou.
            ⚠️ "Todos EXCETO Presidência" sai de graça pelo portão que roda ANTES
               deste (o `cfgRestrito` do PrivateRoute, fonte em perfisRestritos.js):
               os PERFIS RESTRITOS só entram nas rotas do `permite` deles.
               `Presidencia` (só /projetos) e `TI` (só /chamados) ficam de fora;
               `Coordenador de Qualidade` entra porque já tem `/biblioteca` no
               `permite`. Ou seja, abrir aqui não vaza para os restritos.
            ⚠️ Espelha o `canSeeBiblioteca` da Sidebar.js (hoje `true`): divergir
               dá menu sem tela, ou tela sem menu. --- */}
        <Route path="/biblioteca" element={
          <PrivateRoute>
              <Biblioteca />
          </PrivateRoute>
        } />
        {/* O módulo nasceu como `/manuais` em 04/09/2026 e virou `/biblioteca` no
            mesmo dia, ao ganhar a Videoteca. O redirect preserva a QUERY porque é
            nela que mora o link profundo que se manda no grupo da loja
            (`?manual=financeiro-processos&cap=sangria-parcial`) — um `Navigate`
            seco jogaria todo mundo na estante, e ninguém entenderia por quê.
            ⚠️ Fora do `PrivateRoute` de propósito: o portão é o da rota nova. Pôr
               o redirect atrás do portão faria o perfil restrito bater no
               allowlist do caminho VELHO e ser devolvido ao landing. --- */}
        <Route path="/manuais" element={<Navigate to={`/biblioteca${window.location.search}`} replace />} />

        {/* --- Módulo Manutenção (paleta índigo): casco que embute o Programa de
            Certificação de Equipamentos. Perfil "Manutencao". --- */}
        {/* SEM blockLoja: o módulo embute a Validação de Certificação do GERENTE
            (preso à loja), que cai direto na consulta/validação recortada por loja
            no backend. Os demais perfis (Manutencao/Diretoria) veem o módulo inteiro. */}
        <Route path="/manutencao" element={
          <PrivateRoute allowedRoles={['Manutencao', 'Admin', 'Diretoria', 'Gerente', ...perfisDoModulo('manutencao'), ...perfisComAcessoTotal('manutencao')]}>
              <Manutencao />
          </PrivateRoute>
        } />

        {/* --- Módulo Compras (paleta âmbar/amarelo): solicitações de compra de peças
            (nascem do Diagnóstico da Manutenção) + catálogo da Central de Pedidos.
            Perfil "Compras" gerencia; Manutencao/Diretoria acompanham.
            O "Gerente" entrou em 2026-08-17 para acompanhar as peças da PRÓPRIA
            loja (o backend recorta; ver server-compras.js › recorteLoja) — sem
            blockLoja, portanto, que é justamente quem ele é. --- */}
        <Route path="/compras" element={
          /* ⚠️ O `Fiscal` entrou em 2026-09-03: um dos passos da fila de
             Solicitação de Reparo é dele (a devolutiva com a NF), e ele age na
             tela do Compras. Dentro do módulo ele só vê o card de Reparo — o
             recorte fino é do PERMISSIONS do hub. */
          <PrivateRoute allowedRoles={['Compras', 'Admin', 'Manutencao', 'Diretoria', 'Gerente', 'Fiscal', ...perfisDoModulo('compras')]}>
              <Compras />
          </PrivateRoute>
        } />

        {/* --- Módulo Logística (paleta petróleo #0e7490): a Retirada de
            Equipamento — o aparelho que sai da unidade, o comprovante em PDF que
            o acompanha e o retorno (2026-09-10).

            ⚠️⚠️ A funcionalidade NÃO É NOVA: ela existia desde 09/09 espalhada em
            DOIS lugares — um card no hub da Manutenção e uma aba no Chamados
            (para o TI). Esta rota é o lugar único delas. Quem procurar a
            retirada naqueles dois módulos encontra um comentário apontando para
            cá.

            ⚠️ `Logistica` é perfil NOVO e nasce sem usuários: quem opera hoje
            continua sendo TI, Manutenção e o Gerente de loja — todos já na
            lista. Ele existe para o dia em que houver alguém dedicado.
            ⚠️ O `Gerente` fica FORA do blockLoja: ser preso à loja é o que ele é,
            e é dele o ato de pedir que levem o equipamento parado. O recorte é
            do backend (server-logistica.js › escopoPorLoja). --- */}
        <Route path="/logistica" element={
          <PrivateRoute allowedRoles={['Admin', 'Logistica', 'Manutencao', 'Diretoria', 'Gerente', 'Gerente TI', 'TI', ...perfisDoModulo('logistica'), ...perfisComAcessoTotal('logistica')]}>
              <Logistica />
          </PrivateRoute>
        } />

        {/* --- Módulo Marketing (paleta rosa #db2777): o Gerador de Arte de
            Contratação e os Aniversariantes do Mês (2026-09-16).
            ⚠️⚠️ O PERFIL `Marketing` EXISTIA DESDE SEMPRE E NUNCA TEVE TELA —
            ele governa a gestão de feriados do Calendário e o recorte dos
            aniversariantes da Home, mas quem entrava com ele não encontrava
            nada seu. Esta rota é a casa que faltava.
            ⚠️ O `Marketing` chega pelo `perfisDoModulo('marketing')`, não pelo
            nome escrito à mão — é perfil derivado, e a fonte única do recorte é
            o perfisDerivados.js. `Diretoria RH` e `Diretoria Op` ficam fora,
            por decisão (16/09), e o recorte já as barra sozinho.
            ⚠️ SEM blockLoja: as duas telas são da rede inteira (a arte de
            contratação de qualquer loja, os aniversariantes de todas as
            filiais) — não há recorte por loja neste módulo.
            ⚠️⚠️ ABERTO EM 18/09/2026 para a LOJA e para as áreas de gente e de
            operação (`PERFIS_MARKETING`). Quem entra pela lista vê a rede
            INTEIRA — é o desenho do módulo, não um descuido: o gerente que monta
            a arte da própria vaga enxerga o endereço e os aniversariantes das
            outras filiais junto. --- */}
        <Route path="/marketing" element={
          <PrivateRoute allowedRoles={['Admin', 'Diretoria', ...PERFIS_MARKETING, ...perfisDoModulo('marketing')]}>
              <Marketing />
          </PrivateRoute>
        } />

        {/* --- Módulo Demandas (paleta azul-petróleo #156082): o controle do trabalho da
            equipe de dados, que saiu dos 211 checkboxes do PLANO.md (2026-09-17).
            ⚠️ `Admin` (a dupla) tria e trabalha; `Diretoria` (28/09) VÊ a fila e
            ABRE pedido — o recorte fino de quem edita/exclui o quê é do backend
            (`demandas-acesso.js`), não do portão de rota. A fase 3 (demais perfis
            administrativos) ainda não entrou: abrir a porta é compromisso de
            serviço (um pedido parado três semanas vira prova datada).
            ⚠️ A lista aqui tem de casar com `PERFIS_VER` do
            `server/demandas/demandas-acesso.js`: perfil aqui e não lá dá uma
            tela que abre e devolve 403. --- */}
        <Route path="/demandas" element={
          <PrivateRoute allowedRoles={['Admin', 'Diretoria']}>
              <Demandas />
          </PrivateRoute>
        } />
        {/* A demanda tem URL própria (`/demandas/DEM-00001`) e não um painel
            sobre o quadro: URL se guarda, se manda para a dupla e abre em outra
            aba com o clique do meio. O `:codigo` aceita `DEM-00001` e `1` — quem
            traduz é o `interpretarCodigo` do backend. */}
        <Route path="/demandas/:codigo" element={
          <PrivateRoute allowedRoles={['Admin', 'Diretoria']}>
              <DemandaDetalhe />
          </PrivateRoute>
        } />

        {/* --- Módulo Chamados (paleta roxo #6b21a8): abertura de chamado
            para o TI. Substitui o Auvo como fonte única (2026-08-21).
            Quatro papéis: `Gerente`/`Gerente Plantao` abrem, `Gerente TI` tria,
            `TI` executa, e o `Gerente Plantao` valida o encerramento.
            ⚠️ SEM blockLoja: os gerentes são justamente quem abre e valida — o
            recorte por loja acontece no backend (server-chamados.js › recorteLoja),
            como no Manutenção e no Compras.
            ⚠️ `Gerente Adm` NÃO entra (decisão da Julia, 21/08/2026) — é o inverso
            do recorte da agenda; não "consertar" por analogia.
            ⚠️ `Diretoria` entrou em 2026-08-31 com ACESSO TOTAL ao módulo; a
            `Diretoria Op` chega pelo `perfisDoModulo('chamados')`, sem nome
            escrito à mão (`Diretoria RH` e `Marketing` seguem fora).
            ⚠️⚠️ `Coordenador de Qualidade` FALTAVA AQUI — e era ESTE o beco. Ele
            entrou no módulo em 31/08/2026 (equiparado ao Gerente) em todo o resto:
            `perfisRestritos.js`, `PERMISSIONS` do `Chamados.js`, `PERFIS_ABERTURA`
            e `PERFIS_QUE_VALIDAM` do backend — só esta lista ficou de fora. Como
            ele é perfil RESTRITO, o menu dele NÃO consulta o `show` do
            `Sidebar.js` (vem do allowlist do `perfisRestritos.js`), então o item
            *Chamados* aparecia; o clique caía no passo 5 do `PrivateRoute`, era
            mandado para `/home`, que o portão do perfil restrito recusa, e ele
            terminava no `/financeiro`. **Sem erro nenhum na tela** — o sintoma
            ("clico e volto para o Financeiro") não aponta para permissão.
            ⚠️ Fora do `presoLoja` como os gerentes: estar preso à própria loja é o
            que ele é, e o recorte é do backend. --- */}
        {/* --- A PORTA DO QR CODE das etiquetas de patrimônio (2026-09-11).
            `/q/:codigo` resolve o adesivo e encaminha para a abertura com o
            equipamento já escolhido — ver ChamadosEtiquetaEntrada.js.
            ⚠️⚠️ MORA FORA DO `/chamados` DE PROPÓSITO: o adesivo é imutável por
            anos e a rota de um módulo é o que mais muda. O `/q` é o ponto de
            entrada perene; mudar o destino é mudar uma linha, não reimprimir
            1.000 adesivos.
            ⚠️ DENTRO do PrivateRoute porque quem escaneia costuma estar
            DESLOGADO (sessão de 8h, celular do gerente): é o PrivateRoute que
            guarda o destino em `state.from` e o Login que traz a pessoa de
            volta para cá. Fora dele, ela cairia num login sem contexto.
            ⚠️ Mesma lista de perfis do módulo — quem pode abrir chamado pode
            entrar pelo adesivo. --- */}
        <Route path="/q/:codigo" element={
          <PrivateRoute allowedRoles={['Admin', 'Gerente TI', 'TI', 'Gerente', 'Gerente Plantao', 'Coordenador de Qualidade', 'Diretoria', ...perfisDoModulo('chamados')]} capacidade="chamados.modulo">
              <ChamadosEtiquetaEntrada />
          </PrivateRoute>
        } />

        <Route path="/chamados" element={
          <PrivateRoute allowedRoles={['Admin', 'Gerente TI', 'TI', 'Gerente', 'Gerente Plantao', 'Coordenador de Qualidade', 'Diretoria', ...perfisDoModulo('chamados')]} capacidade="chamados.modulo">
              <Chamados />
          </PrivateRoute>
        } />

        {/* --- Módulo Fiscal (paleta roxa): casco que embute Apuração de Impostos e
            Auditoria Fiscal. Substitui o antigo hub de Automações (descontinuado).
            blockLoja: barra usuário preso à loja. --- */}
        <Route path="/fiscal" element={
          <PrivateRoute blockLoja allowedRoles={['Fiscal', 'Admin', 'Gerente Adm', 'Diretoria']}>
              <Fiscal />
          </PrivateRoute>
        } />

        {/* O hub /automacoes foi descontinuado. As telas fiscais viraram o módulo /fiscal;
            as telas contábeis, o módulo /contabilidade; as automações de Dados (Sovos,
            Loyalty) foram ocultadas. Redirecionamentos preservam links/bookmarks antigos. */}
        <Route path="/automacoes" element={<Navigate to="/fiscal" replace />} />
        <Route path="/automacoes/auditoria-fiscal" element={<Navigate to="/fiscal" replace />} />
        <Route path="/automacoes/apuracao-impostos" element={<Navigate to="/fiscal" replace />} />
        {/* ⚠️ `/automacoes/fechamento` continua apontando para o módulo, mas a tela que ele
            prometia (Fechamento visão Contábil) foi ARQUIVADA em 2026-09-01 — ver
            backup/contabilidade/relatorio-fechamento/. Mandar para /contabilidade é melhor
            que 404: o link antigo cai num módulo que existe, em vez de numa tela quebrada. */}
        <Route path="/automacoes/fechamento" element={<Navigate to="/contabilidade" replace />} />
        <Route path="/automacoes/conferencia-vendas" element={<Navigate to="/contabilidade" replace />} />
        <Route path="/automacoes/pl-zscore" element={<Navigate to="/contabilidade" replace />} />
        <Route path="/automacoes/rtb12-simplesnacional" element={<Navigate to="/contabilidade" replace />} />

      {/* --- Módulo Contabilidade (paleta verde): casco que embute RTB12, P&L e
          Conferência. Mesmos perfis das telas antigas (união).
          ⚠️ `Financeiro` SAIU em 2026-09-01: ele só alcançava este módulo pela aba
          Relatórios (Fechamento Contábil), que foi arquivada. Sem ela, entraria num
          módulo sem nenhuma aba visível — o Sidebar já não lhe mostrava o item. O
          Fechamento que interessa a ele é o do próprio Financeiro, que segue no ar. --- */}
      <Route path="/contabilidade" element={
          <PrivateRoute blockLoja allowedRoles={['Contabil', 'Gerente Adm', 'Admin', 'Diretoria', 'Fiscal']}>
              <Contabilidade />
          </PrivateRoute>
      } />
      
        {/* --- Rota Página Admin : Acessível apenas por 'Admin' --- */}
        <Route
          path="/admin/dashboard"
          element={
            <PrivateRoute allowedRoles={['Admin']}>
              <AdminDashboard />
            </PrivateRoute>
          }
        />

        {/* Módulo Treinamento & Qualidade (ex-"Formulários", seção de qualidade).
            Os formulários (Auditoria SA, Avaliação TSV) ficam embutidos no submódulo
            "Formulários" do hub — sem rota própria, igual ao módulo RH. */}
        {/* RH entra no Treinamento por causa do PPI (migrado do RH em 2026-07-15):
            opera Processos & Vagas + Triagem. O card de Formulários não aparece para
            RH (a permissão dele exclui RH). */}
        <Route path="/treinamento" element={
            <PrivateRoute allowedRoles={['Treinamento', 'Admin', 'Diretoria', 'RH', ...perfisDoModulo('treinamento'), ...perfisComAcessoTotal('treinamento')]}>
                <Treinamento />
            </PrivateRoute>
        } />

        {/* --- Módulo Operacional: hub de ferramentas da operação (Avaliação 360,
            Autoverificação de Fechamento Mensal, Auditoria Financeira e de
            Estoques). Perfil "Operacional" preenche a 360; o "Gerente" CONSULTA.
            ⚠️ SEM blockLoja desde 2026-08-26: o Gerente entrou para ver a
            avaliação da PRÓPRIA loja, e o blockLoja barra exatamente quem tem
            loja — ou seja, barraria justamente ele. O recorte não se perde: ele
            é do backend (server-bsv.js › escopoLojaSql/podeVerLoja), e o hub
            esconde "Nova Avaliação"/"Gerenciar versões" do Gerente. Mesmo
            desenho do Manutenção, Compras e Chamados.
            ⚠️⚠️ `Auditoria` e `Gerente Adm` entraram em 2026-09-23 (DEM-00011,
            Fase 4, T21) — SÓ por causa do Checklist (Auditoria Financeira e de
            Estoques), que mudou de módulo. Eles já tinham acesso à FERRAMENTA
            (`/api/auditoria/checklist` não mudou); o que muda é a PORTA de
            entrada. Preservado da lista antiga (`Auditoria.js › PERMISSIONS.
            forms`, antes desta migração).
            ⚠️⚠️ `Treinamento` entrou no mesmo dia (T22), pelo mesmo motivo, com
            a Avaliação de Segurança do Alimento (ex-Auditoria SA): o perfil já
            alcançava a ferramenta lá no Treinamento e ficaria sem porta de
            entrada aqui se não viesse junto — mesmo raciocínio do `Auditoria`/
            `Gerente Adm` acima. --- */}
        <Route path="/operacional" element={
            <PrivateRoute allowedRoles={['Operacional', 'Admin', 'Diretoria', 'Gerente', 'Auditoria', 'Gerente Adm', 'Treinamento', ...perfisDoModulo('operacional')]}>
                <Operacional />
            </PrivateRoute>
        } />

        {/* Módulo Medicina do Trabalho (ex-"Formulários", seção de medicina).
            Atestados Médicos ficam embutidos no submódulo "Formulários" do hub.
            ⚠️ `Manutencao` entra só na CONSULTA, e vendo apenas os atestados dos
            cargos de manutenção — o recorte de verdade é do servidor. */}
        <Route path="/medicina" element={
            <PrivateRoute allowedRoles={['Medico', 'RH', 'Gerente', 'Admin', 'Diretoria', 'Manutencao', ...perfisDoModulo('medicina'), ...perfisComAcessoTotal('medicina')]}>
                <Medicina />
            </PrivateRoute>
        } />
    
        {/* --- Módulo Projetos (paleta violeta): Inventário Executivo do CEO.
            ⚠️ Autoriza por USUÁRIO (podeAcessar), não por perfil — o inventário é
            o acompanhamento pessoal do Sr. Marco, e o perfil dele (Diretoria) tem
            8 pessoas. Quem protege o dado é o backend; isto só fecha a rota. --- */}
        <Route path="/projetos" element={
            <PrivateRoute podeAcessar={podeVerProjetos}>
                <Projetos />
            </PrivateRoute>
        } />

        <Route path="/p15-externo/:token" element={<Primeiro15Externo/>} />

        <Route path="/pesquisaclima/:token" element={<PesquisaExperienciaColaboradorExterno/>} />

        {/* Pesquisa de Escala (12x36 × 5x2) — link anônimo por WhatsApp, sem login.
            ⚠️ O caminho é parte do link que já saiu no celular das pessoas: mudar
            /pesquisaescala quebra todos os links de uma campanha em andamento. */}
        <Route path="/pesquisaescala/:token" element={<PesquisaEscalaExterno/>} />

        {/* Inscrição pública do PPI (Promoção Interna) — sem login */}
        <Route path="/ppi-inscricao/:token" element={<PPIInscricao/>} />

        {/* Prova do PPI — aberta pelo QR da sala; o candidato se identifica pelo CPF */}
        <Route path="/ppi-prova/:token" element={<PPIProvaPublica/>} />

        {/* Prova do PPI por PROCESSO — QR ÚNICO da sala; o CPF resolve a trilha */}
        <Route path="/ppi-prova-processo/:token" element={<PPIProvaPublica porProcesso/>} />

        {/* Projeção da sala (relógio + QR único) — o aplicador abre no telão. Autenticada. */}
        <Route path="/ppi-projecao/:id" element={
            <PrivateRoute allowedRoles={['Treinamento', 'Admin', 'Diretoria', 'RH', ...perfisDoModulo('treinamento'), ...perfisComAcessoTotal('treinamento')]}>
                <PPIProjecao/>
            </PrivateRoute>
        } />

        {/*Tela do Financeiro*/}
        <Route path="/financeiro" element={
            <PrivateRoute allowedRoles={['Admin','Gerente','Financeiro','Gerente Adm','Gerente Plantao','Diretoria','Auditoria','Coordenador de Qualidade', ...perfisDoModulo('financeiro'), ...perfisComAcessoTotal('financeiro')]}>
                <Financeiro />
            </PrivateRoute>
        } />

        {/*Módulo de Recursos Humanos*/}
        <Route path="/rh" element={
            <PrivateRoute allowedRoles={['RH','Admin','Diretoria','Gerente','Gerente Plantao','EntrevistaDesligamento','Treinamento','Coordenador de Qualidade', ...perfisDoModulo('rh'), ...perfisComAcessoTotal('rh')]}>
                <RH />
            </PrivateRoute>
        } />
        {/* Gestão de ETL */}
        <Route path="/etl" element={
            <PrivateRoute allowedRoles={['Admin']}>
                <ETLPanel />
            </PrivateRoute>
        } />
        {/*Tela de Proceso Seletivo */}
        <Route path="/processoseletivo" element={
            <PrivateRoute blockLoja allowedRoles={['RH', 'Gerente','ProcessoSeletivo', 'Admin','Diretoria','Medico', ...perfisDoModulo('processoseletivo')]}>
                <ProcessoSeletivo />
            </PrivateRoute>
        } />
        <Route path="/processoseletivo/etapa1" element={
            <PrivateRoute allowedRoles={['RH', 'Gerente','ProcessoSeletivo', 'Admin','Diretoria', ...perfisDoModulo('processoseletivo')]}>
                <ProcessoSeletivoTriagem />
            </PrivateRoute>
        } />
        {/* Rota padrão: se o usuário digitar qualquer outra coisa, é redirecionado para /home */}
        <Route
          path="/*"
          element={<Navigate to="/home" />} />
      </Routes>
    </Router>
  );
}

export default App;